import { forceCollide, forceLink, forceManyBody, forceSimulation, forceX, forceY, type SimulationNodeDatum } from "d3-force";
import { LitElement, html, svg } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import type { ContentPayload, Study } from "../types";
import { profileMap, techniqueMap } from "../services/content";

interface GraphNode extends SimulationNodeDatum { id: string; study: Study; x: number; y: number }

@customElement("graph-page")
export class GraphPage extends LitElement {
  @property({ attribute: false }) content!: ContentPayload;
  @state() private selected?: Study;
  @state() private filter = "all";
  @state() private nodes: GraphNode[] = [];
  private width = 1180;
  private height = 650;
  protected createRenderRoot() { return this; }

  willUpdate(changed: Map<PropertyKey, unknown>): void {
    if (changed.has("content") || changed.has("filter")) this.layout();
  }

  private layout(): void {
    if (!this.content) return;
    const studies = this.content.catalog.studies.filter(
      (study) => this.filter === "all" || study.techniques.includes(this.filter),
    );
    const ids = new Set(studies.map((study) => study.slug));
    const techniqueIndex = new Map(this.content.catalog.techniques.map((technique, index) => [technique.slug, index]));
    const nodes: GraphNode[] = studies.map((study) => ({
      id: study.slug,
      study,
      x: 70 + ((study.year - 1966) / (2013 - 1966)) * (this.width - 140),
      y: 70 + ((techniqueIndex.get(study.techniques[0]) ?? 0) / 7) * (this.height - 140),
    }));
    const links = this.content.catalog.edges.filter((edge) => ids.has(edge.from) && ids.has(edge.to)).map((edge) => ({ source: edge.from, target: edge.to }));
    const simulation = forceSimulation(nodes)
      .randomSource(() => 0.417)
      .force("link", forceLink<GraphNode, {source: string | GraphNode; target: string | GraphNode}>(links).id((node) => node.id).distance(78).strength(0.22))
      .force("charge", forceManyBody().strength(-115))
      .force("x", forceX<GraphNode>((node) => 70 + ((node.study.year - 1966) / 47) * (this.width - 140)).strength(0.72))
      .force("y", forceY<GraphNode>((node) => 70 + ((techniqueIndex.get(node.study.techniques[0]) ?? 0) / 7) * (this.height - 140)).strength(0.13))
      .force("collide", forceCollide(34))
      .stop();
    for (let i = 0; i < 220; i += 1) simulation.tick();
    this.nodes = [...nodes];
  }

  render() {
    const profiles = profileMap(this.content);
    const techniques = techniqueMap(this.content);
    const nodeById = new Map(this.nodes.map((node) => [node.id, node]));
    const visibleEdges = this.content.catalog.edges.filter((edge) => nodeById.has(edge.from) && nodeById.has(edge.to));
    const selectedEdges = this.selected ? this.content.catalog.edges.filter((edge) => edge.from === this.selected!.slug || edge.to === this.selected!.slug) : [];
    return html`
      <main class="graph-page">
        <header class="graph-header">
          <div><span class="eyebrow">1966—2013 / 30 performances</span><h1>The beat evolves.</h1><p>Horizontal position is time. Vertical pull comes from shared rhythmic vocabulary. Every line describes a drum idea—not a claim that one whole band created another.</p></div>
        </header>
        <div class="technique-filters">
          <button class=${this.filter === "all" ? "active" : ""} @click=${() => { this.filter = "all"; }}>All vocabulary</button>
          ${this.content.catalog.techniques.map((technique) => html`<button class=${this.filter === technique.slug ? "active" : ""} style="--tech:${technique.color}" @click=${() => { this.filter = technique.slug; }}><i></i>${technique.label}</button>`)}
        </div>
        <div class="graph-workspace">
          <div class="graph-canvas">
            <svg viewBox="0 0 ${this.width} ${this.height}" role="img" aria-label="Force-directed timeline of metal drum performances">
              <g class="era-lines">${[1970, 1980, 1990, 2000, 2010].map((year) => { const x = 70 + ((year - 1966) / 47) * (this.width - 140); return svg`<line x1=${x} x2=${x} y1="28" y2=${this.height - 20}></line><text x=${x + 7} y="24">${year}s</text>`; })}</g>
              <g class="edges">${visibleEdges.map((edge) => { const from = nodeById.get(edge.from)!; const to = nodeById.get(edge.to)!; return svg`<line x1=${from.x} y1=${from.y} x2=${to.x} y2=${to.y} class=${edge.evidence}></line>`; })}</g>
              <g class="nodes">${this.nodes.map((node) => { const color = techniques.get(node.study.techniques[0])?.color ?? "#aaa"; return svg`
                <g class="graph-node ${node.study.status}" transform="translate(${node.x} ${node.y})" role="button" tabindex="0" @click=${() => { this.selected = node.study; }} @keydown=${(event: KeyboardEvent) => { if (event.key === "Enter" || event.key === " ") this.selected = node.study; }}>
                  <circle r=${node.study.status === "playable" ? 16 : node.study.status === "pilot" ? 13 : 10} fill=${color}></circle>
                  <text y="28" text-anchor="middle">${node.study.band}</text><text class="year" y="40" text-anchor="middle">${node.study.year}</text>
                </g>`; })}</g>
            </svg>
          </div>
          <div class="graph-list" aria-label="Chronological performance list">
            ${[...this.nodes].sort((a, b) => a.study.year - b.study.year).map((node) => html`<button @click=${() => { this.selected = node.study; }}><span>${node.study.year}</span><b>${node.study.band}</b><small>${node.study.track}</small></button>`)}
          </div>
          <aside class="node-preview ${this.selected ? "open" : ""}">
            ${this.selected ? html`
              <button class="close" aria-label="Close preview" @click=${() => { this.selected = undefined; }}>×</button>
              <span class="eyebrow">${this.selected.year} · ${this.selected.status === "playable" ? "Playable lesson" : "Coming later"}</span>
              <h2>${this.selected.band}</h2><h3>${this.selected.track}</h3>
              <p class="drummer">${profiles.get(this.selected.drummer)?.name} · ${profiles.get(this.selected.drummer)?.band}</p>
              <p>${this.selected.focus}</p>
              <div class="tag-row">${this.selected.techniques.map((slug) => html`<span style="--tech:${techniques.get(slug)?.color}">${techniques.get(slug)?.label}</span>`)}</div>
              <div class="preview-alts"><small>Contrasting studies</small>${this.selected.alternates.map((slug) => html`<span><b>${profiles.get(slug)?.name}</b> · ${profiles.get(slug)?.band}</span>`)}</div>
              <div class="edge-notes"><small>Rhythmic connections</small>${selectedEdges.slice(0, 4).map((edge) => html`<details><summary>${edge.evidence === "documented" ? "Documented" : "Editorial inference"} · ${techniques.get(edge.technique)?.label}</summary><p>${edge.note}</p>${edge.source ? html`<a href=${edge.source} target="_blank" rel="noopener">Read source ↗</a>` : ""}</details>`)}</div>
              <a class="button primary" href="/studies/${this.selected.slug}">${this.selected.status === "playable" ? "Open interactive study" : "Open overview"}</a>
            ` : html`<span class="eyebrow">Select a node</span><h2>Follow a rhythmic idea.</h2><p>Solid edges are documented relationships; dashed edges are clearly labeled editorial inferences.</p>`}
          </aside>
        </div>
        <section class="research-shelf" aria-labelledby="research-heading">
          <div><span class="eyebrow">Editorial method</span><h2 id="research-heading">A listening map, with receipts.</h2><p>The chronology and performance details are researched; dashed relationships are our listening-based comparisons, not claims of direct influence. Solid lines only appear where a musician or specialist source makes the relationship explicit.</p></div>
          <div class="research-links">${this.content.catalog.sources.map((source) => html`<a href=${source.url} target="_blank" rel="noopener"><b>${source.title}</b><span>${source.publisher} ↗</span></a>`)}</div>
        </section>
      </main>
    `;
  }
}
